import React from "react";
import { motion } from "framer-motion";
import { ArrowUp, BarChart, Maximize, Lightbulb } from "lucide-react";

const WhatWeDo = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-[#e2e8f0]/40"
            style={{
              width: `${Math.random() * 80 + 20}px`,
              height: `${Math.random() * 80 + 20}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -15, 0],
              x: [0, 10, 0],
            }}
            transition={{
              duration: 15 + Math.random() * 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-gray-800 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            What We Do
          </motion.h2>

          <motion.p
            className="text-xl text-gray-600 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            At AAIDAN K, we help your startup succeed by delivering results that
            push it forward.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Card 1: Increase Your Rate of Growth */}
          <motion.div
            className="bg-white border border-gray-200 rounded-2xl shadow-lg p-8 flex flex-col h-full"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ y: -10 }}
          >
            <div className="bg-red-50 p-4 rounded-full w-14 h-14 flex items-center justify-center mb-6">
              <ArrowUp className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Increase Your Rate of Growth
            </h3>
            <p className="text-gray-600 flex-grow">
              We help you find and seize development opportunities sooner with
              real-time analytics and strategic advice.
            </p>
          </motion.div>

          {/* Card 2: Maximize Your Assessment */}
          <motion.div
            className="bg-white border border-gray-200 rounded-2xl shadow-lg p-8 flex flex-col h-full"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            whileHover={{ y: -10 }}
          >
            <div className="bg-blue-50 p-4 rounded-full w-14 h-14 flex items-center justify-center mb-6">
              <Maximize className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Maximize Your Assessment
            </h3>
            <p className="text-gray-600 flex-grow">
              Our seasoned financial management enhances your performance,
              making your startup attractive to investors.
            </p>
          </motion.div>

          {/* Card 3: Realize Your Complete Potential */}
          <motion.div
            className="bg-white border border-gray-200 rounded-2xl shadow-lg p-8 flex flex-col h-full"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
            whileHover={{ y: -10 }}
          >
            <div className="bg-green-50 p-4 rounded-full w-14 h-14 flex items-center justify-center mb-6">
              <Lightbulb className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Realize Your Complete Potential
            </h3>
            <p className="text-gray-600 flex-grow">
              We make it easier for you to focus on your business goals by
              handling financial administration and offering you strategic
              ideas.
            </p>
          </motion.div>
        </div>

        {/* Decorative elements */}
        <motion.div
          className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-red-100/30 blur-3xl z-0"
          animate={{
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        <motion.div
          className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-blue-100/30 blur-3xl z-0"
          animate={{
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
        />
      </div>
    </section>
  );
};

export default WhatWeDo;
